
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UsePhoneVerificationProps {
  inviteId?: string;
  guestName: string;
  onSuccess: () => void;
}

export const usePhoneVerification = ({
  inviteId,
  guestName,
  onSuccess
}: UsePhoneVerificationProps) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [cooldownTime, setCooldownTime] = useState(0);
  const [showNameMismatchDialog, setShowNameMismatchDialog] = useState(false);
  const [existingUserName, setExistingUserName] = useState("");
  const [nameMismatchConfirmed, setNameMismatchConfirmed] = useState(false);
  const [verificationState, setVerificationState] = useState<{
    step: 'phone_entry' | 'code_entry' | 'processing' | 'success' | 'error';
    error: string | null;
  }>({
    step: 'phone_entry',
    error: null
  });
  const { toast } = useToast();

  // Reset cooldown timer
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    
    if (cooldownTime > 0) {
      interval = setInterval(() => {
        setCooldownTime(prev => Math.max(0, prev - 1));
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldownTime]);

  // State checks for UI rendering
  const isPhoneEntryStep = useCallback(() => 
    verificationState.step === 'phone_entry', [verificationState]);
  
  const isCodeEntryStep = useCallback(() => 
    verificationState.step === 'code_entry', [verificationState]);
  
  const isProcessingPhone = useCallback(() => 
    verificationState.step === 'processing' && isPhoneEntryStep(), [verificationState, isPhoneEntryStep]);
  
  const isProcessingCode = useCallback(() => 
    verificationState.step === 'processing' && isCodeEntryStep(), [verificationState, isCodeEntryStep]);

  // Internal implementation of phone submission logic
  const handlePhoneSubmitInternal = useCallback(async () => {
    if (!phoneNumber) return;
    
    setVerificationState({
      step: 'processing',
      error: null
    });
    
    try {
      // Send OTP 
      const { error } = await supabase.auth.signInWithOtp({
        phone: phoneNumber,
        options: {
          data: {
            name: guestName,
            source: 'event_invite',
            invite_id: inviteId
          }
        }
      });
      
      if (error) throw error;
      
      setCooldownTime(60); // 1 minute cooldown for resend
      setVerificationState({
        step: 'code_entry',
        error: null
      });
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      setVerificationState({
        step: 'phone_entry',
        error: error.message || "Failed to send verification code"
      });
    }
  }, [phoneNumber, guestName, inviteId]);

  // Handle phone number submission and OTP sending
  const handlePhoneSubmit = useCallback(async () => {
    if (!phoneNumber) return;
    
    setVerificationState({
      step: 'processing',
      error: null
    });
    
    try {
      // Check if the phone is already registered with a different name
      const { data: existingUser, error: userError } = await supabase
        .from('users')
        .select('full_name, user_id')
        .eq('phone_number', phoneNumber)
        .maybeSingle();
      
      if (userError) throw userError;
      
      // If phone belongs to a user with a different name
      if (existingUser && existingUser.full_name !== guestName) {
        setExistingUserName(existingUser.full_name);
        setShowNameMismatchDialog(true);
        setVerificationState({
          step: 'phone_entry',
          error: null
        });
        return;
      }
      
      // No name mismatch, proceed with OTP directly
      await handlePhoneSubmitInternal();
    } catch (error: any) {
      console.error('Error in phone verification flow:', error);
      setVerificationState({
        step: 'phone_entry',
        error: error.message || "Failed to verify phone number"
      });
    }
  }, [phoneNumber, guestName, handlePhoneSubmitInternal]);

  // Link invite to user (after verification)
  const linkInviteToUser = useCallback(async (userId: string): Promise<boolean> => {
    if (!inviteId) return true;
    
    try {
      console.log(`Linking invite ${inviteId} to user ${userId}`);
      
      const { error } = await supabase
        .from('event_invites')
        .update({
          claimed: true,
          claimed_at: new Date().toISOString(),
          claimed_by_user_id: userId,
          phone_number: phoneNumber, // Keep the phone number for reference
          responded_at: new Date().toISOString()
        })
        .eq('invite_id', inviteId);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error linking invite to user:', error);
      return false;
    }
  }, [inviteId, phoneNumber]);

  // Handle verification code submission
  const handleCodeSubmit = useCallback(async () => {
    if (verificationCode.length !== 6) return;
    
    setVerificationState({
      step: 'processing',
      error: null
    });
    
    try {
      // Verify OTP and get user ID
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phoneNumber,
        token: verificationCode,
        type: 'sms'
      });
      
      if (error) throw error;
      
      // Get user ID from response
      const userId = data.user?.id;
      if (!userId) throw new Error("No user ID found after verification");
      
      // Update user record to mark as validated
      // For existing users who confirmed a name mismatch - we do NOT update their name
      const { error: updateError } = await supabase
        .from('users')
        .update({
          validated: true,
          // Only update name for new users, which won't have a name mismatch dialog shown
          ...(nameMismatchConfirmed ? {} : {}) // Empty object means no name update
        })
        .eq('phone_number', phoneNumber);
      
      if (updateError) console.error('Error updating user validation:', updateError);
      
      // Link invite to user
      const linkSuccess = await linkInviteToUser(userId);
      if (!linkSuccess) {
        throw new Error("Failed to link invite to user");
      }
      
      // Record feedback_started_by with the session ID after successful verification
      const sessionId = crypto.randomUUID();
      if (inviteId) {
        const { error: feedbackStartError } = await supabase
          .from('event_invites')
          .update({
            feedback_started: new Date().toISOString(),
            feedback_started_by: sessionId
          })
          .eq('invite_id', inviteId);
        
        if (feedbackStartError) {
          console.error('Error marking feedback as started:', feedbackStartError);
          // Continue even if this fails, as it's not critical
        }
      }
      
      setVerificationState({
        step: 'success',
        error: null
      });
      
      toast({
        title: "Success",
        description: "Phone number verified successfully!"
      });
      
      // Call the success callback after a short delay to allow toast to be shown
      setTimeout(() => {
        onSuccess();
      }, 800);
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setVerificationState({
        step: 'code_entry',
        error: error.message || "Failed to verify code"
      });
    }
  }, [verificationCode, phoneNumber, linkInviteToUser, onSuccess, toast, nameMismatchConfirmed, inviteId]);

  // Handle resend code
  const handleResendCode = useCallback(async () => {
    if (cooldownTime > 0) return;
    
    setVerificationState({
      step: 'processing',
      error: null
    });
    
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone: phoneNumber,
        options: {
          data: {
            name: guestName,
            source: 'event_invite',
            invite_id: inviteId
          }
        }
      });
      
      if (error) throw error;
      
      setCooldownTime(60); // 1 minute cooldown
      setVerificationState({
        step: 'code_entry',
        error: null
      });
      
      toast({
        title: "Code sent",
        description: "Verification code has been resent to your phone"
      });
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      setVerificationState({
        step: 'code_entry',
        error: error.message || "Failed to resend verification code"
      });
    }
  }, [phoneNumber, cooldownTime, guestName, inviteId, toast]);

  // Reset verification process
  const resetVerification = useCallback(() => {
    setVerificationState({
      step: 'phone_entry',
      error: null
    });
    setVerificationCode("");
    setNameMismatchConfirmed(false);
  }, []);

  // Handle name mismatch resolution - continue with new name
  const handleContinueWithNewName = useCallback(() => {
    setShowNameMismatchDialog(false);
    setNameMismatchConfirmed(true);
    // Direct call to handle phone submission internal instead of using effect
    handlePhoneSubmitInternal();
  }, [handlePhoneSubmitInternal]);

  // Handle name mismatch resolution - change phone
  const handleChangePhone = useCallback(() => {
    setShowNameMismatchDialog(false);
    setNameMismatchConfirmed(false);
    resetVerification();
  }, [resetVerification]);

  return {
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    verificationState,
    cooldownTime,
    showNameMismatchDialog,
    existingUserName,
    handlePhoneSubmit,
    handleCodeSubmit,
    handleResendCode,
    resetVerification,
    handleContinueWithNewName,
    handleChangePhone,
    setShowNameMismatchDialog,
    // State machine accessors
    isPhoneEntryStep,
    isCodeEntryStep,
    isProcessingPhone,
    isProcessingCode
  };
};
